import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MetafieldsState,
  Metafield,
  CreateMetafieldDto,
  UpdateMetafieldDto,
} from "@/types/models/metafield";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

const initialState: MetafieldsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    pageSize: 10,
    total: 0,
  },
  formState: {
    loading: false,
    error: null,
    success: false,
  },
  keyValidation: {
    loading: false,
    isUnique: null,
    lastCheckedKey: null,
    error: null,
  },
};

const metafieldSlice = createSlice({
  name: "metafields",
  initialState,
  reducers: {
    // Fetch metafields
    fetchMetafieldsRequest: (
      state,
      _action: PayloadAction<{ page?: number; limit?: number }>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchMetafieldsSuccess: (
      state,
      action: PayloadAction<{ data: Metafield[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.pagination?.page || 1,
        totalPages: action.payload.pagination?.totalPages || 1,
        pageSize: action.payload.pagination?.pageSize || 10,
        total: action.payload.pagination?.total || action.payload.data.length,
      };
      state.error = null;
    },
    fetchMetafieldsFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    // Create metafield
    createMetafieldRequest: (
      state,
      _action: PayloadAction<CreateMetafieldDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createMetafieldSuccess: (state, action: PayloadAction<Metafield>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items.unshift(action.payload);
      state.pagination.total += 1;
    },
    createMetafieldFailure: (state, action: PayloadAction<any>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Update metafield
    updateMetafieldRequest: (
      state,
      _action: PayloadAction<{ key: string; data: UpdateMetafieldDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateMetafieldSuccess: (state, action: PayloadAction<Metafield>) => {
      state.formState.loading = false;
      state.formState.success = true;
      const index = state.items.findIndex(
        (item) => item.key === action.payload.key,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateMetafieldFailure: (state, action: PayloadAction<any>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Delete metafield
    deleteMetafieldRequest: (state, _action: PayloadAction<{ id: string }>) => {
      state.loading = true;
      state.error = null;
    },
    deleteMetafieldSuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },
    deleteMetafieldFailure: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Reset form state
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },

    // Check metafield key uniqueness
    checkMetafieldKeyRequest: (
      state,
      _action: PayloadAction<{ key: string; applied_to: string }>,
    ) => {
      state.keyValidation.loading = true;
      state.keyValidation.error = null;
    },
    checkMetafieldKeySuccess: (
      state,
      action: PayloadAction<{ key: string; isUnique: boolean }>,
    ) => {
      state.keyValidation.loading = false;
      state.keyValidation.isUnique = action.payload.isUnique;
      state.keyValidation.lastCheckedKey = action.payload.key;
      state.keyValidation.error = null;
    },
    checkMetafieldKeyFailure: (
      state,
      action: PayloadAction<{ key: string; error: string }>,
    ) => {
      state.keyValidation.loading = false;
      state.keyValidation.isUnique = null;
      state.keyValidation.lastCheckedKey = action.payload.key;
      state.keyValidation.error = action.payload.error;
    },

    // Reset key validation
    resetKeyValidation: (state) => {
      state.keyValidation = {
        loading: false,
        isUnique: null,
        lastCheckedKey: null,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchMetafieldsRequest,
  fetchMetafieldsSuccess,
  fetchMetafieldsFailure,
  createMetafieldRequest,
  createMetafieldSuccess,
  createMetafieldFailure,
  updateMetafieldRequest,
  updateMetafieldSuccess,
  updateMetafieldFailure,
  deleteMetafieldRequest,
  deleteMetafieldSuccess,
  deleteMetafieldFailure,
  resetFormState,
  checkMetafieldKeyRequest,
  checkMetafieldKeySuccess,
  checkMetafieldKeyFailure,
  resetKeyValidation,
} = metafieldSlice.actions;

export default metafieldSlice.reducer;
